import { ViewTimeInference } from "@/dataprocessor/view-time";
import type { InferenceType } from "../types/inference";
import { fetchElementTags } from "./../utils/fetch-tags";
import { Infer } from "@/dataprocessor/infer";
import { EventListener } from "@/dataprocessor/event-listener";
import { DataStore } from "@/datastore";
import { Init } from "@/datastore/init";
import { ScoringHandler } from "@/dataprocessor/scoring";
import { InferenceEngineProvider } from "../engine/attributes";
import { CartObserverProvider } from "../engine/cart";
import { BrandSettingsProvider } from "@/providers/brand-settings";
import { RetargetingPlugsProvider } from "@/providers/retargeting-plugs";
import { Memory } from "@/memory";
import { BrandSpecificProvider } from "@/brand-specific";

export const InferenceSetup = {
  initialise() {
    InferenceSetup.constructor();

    InferenceSetup.overrideHistoryChange(
      () => InferenceSetup.constructor(),
      () => InferenceSetup.flush(),
    );
  },

  constructor() {
    BrandSpecificProvider.init();

    Init.initPageData();

    Init.init();

    DataStore.init();

    //    if (!window.location.search.includes("h_dev")) return;
    CartObserverProvider.init();

    BrandSettingsProvider.init();

    RetargetingPlugsProvider.init();

    InferenceSetup.loadInferredAttributesAfterNavigation();

    setTimeout(() => {
      EventListener.initialise();
    }, 0);

    Infer.initialise();

    InferenceSetup.landingTimestamp();

    InferenceSetup.tagPageElements();

    InferenceSetup.generateTokens();

    InferenceSetup.registerPathnameInJourney();

    setTimeout(() => {
      Memory.init();
    }, 1000);

    InferenceEngineProvider.init();
    console.log("INITIALIZING INFERENCE ENGINE");

    InferenceEngineProvider.infer();
  },

  landingTimestamp() {
    const currentTimestamp = Date.now();

    if (!sessionStorage.getItem("landingTimestamp")) {
      sessionStorage.setItem("landingTimestamp", currentTimestamp.toString());
    }
    return sessionStorage.getItem("landingTimestamp");
  },

  flush() {
    InferenceSetup.getIntervals().forEach((id) => clearInterval(id));
    InferenceSetup.getTimeouts().forEach((id) => clearTimeout(id));

    window.heliumIntervalIds = [];
    window.heliumTimeoutIds = [];

    EventListener.cleanup();
    InferenceSetup.persistInferredAttributesBeforeNavigation();
  },

  killswitch() {
    return;
    // if (
    //   [
    //     "localhost",
    //     "127.0.0.1",
    //     "flare.heliumbuilder.com",
    //     "culture-circle",
    //     "intelligence-dashboard-git-feat-events-helium-team.vercel.app",
    //   ].some((e) => window.location.hostname.includes(e))
    // ) {
    //   return;
    // }

    // if (window.helium) {
    //   delete window.helium;
    // }

    // if (window.heliumIntervalIds) {
    //   InferenceSetup.getIntervals().forEach((intervalId) =>
    //     clearInterval(intervalId),
    //   );

    //   delete window.heliumIntervalIds;
    // }

    // if (window.heliumTimeoutIds) {
    //   InferenceSetup.getTimeouts().forEach((timeoutId) =>
    //     clearTimeout(timeoutId),
    //   );

    //   delete window.heliumTimeoutIds;
    // }

    // throw new Error("Helium SDK Killswitch Activated");
  },

  persistInferredAttributesBeforeNavigation() {
    const attributes = window.helium.attributes || {};
    const journey = window.helium.logs || [];

    if (!attributes || !journey) return;

    try {
      sessionStorage.setItem(
        "helium-attributes-temp",
        JSON.stringify({ attributes, journey }),
      );
    } catch (e) {
      console.log("SESSION STORAGE ERRORED ", e);
    }
  },

  loadInferredAttributesAfterNavigation() {
    console.log("LOADING ATTRIBUTES AFTER NAVIGATION");
    const stringified =
      sessionStorage.getItem("helium-attributes-temp") || null;

    if (!stringified) return;

    try {
      const { attributes, journey } = JSON.parse(stringified);

      window.helium.attributes = attributes;
      window.helium.logs = journey;
    } catch (e) {
      console.error("NOT IN JSON FORMAT");
    }
  },

  persistInferenceLocally() {
    const inference = ViewTimeInference.inference;
    const behavioralInference = ViewTimeInference.behavioralInference || {};

    if (!inference) return;

    try {
      sessionStorage.setItem("inference", JSON.stringify(inference));
      sessionStorage.setItem(
        "behavioralInference",
        JSON.stringify(behavioralInference),
      );
    } catch (e) {
      console.log("SESSION STORAGE ERRORED ", e);
    }
  },

  getBehavioralInferenceFromLocalStorage() {
    const inference = sessionStorage.getItem("behavioralInference") || null;

    if (!inference) {
      return {
        age: { order: [], details: {} },
        accessTimeAge: { order: [], details: {} },
        deviceAge: { order: [], details: {} },
        scrollSpeedAge: { order: [], details: {} },
        viewTimeAge: { order: [], details: {} },
      };
    }

    try {
      return (
        JSON.parse(inference) || {
          age: { order: [], details: {} },
          accessTimeAge: { order: [], details: {} },
          deviceAge: { order: [], details: {} },
          scrollSpeedAge: { order: [], details: {} },
          viewTimeAge: { order: [], details: {} },
        }
      );
    } catch (e) {
      console.error("NOT IN JSON FORMAT");
      return {
        age: { order: [], details: {} },
        accessTimeAge: { order: [], details: {} },
        deviceAge: { order: [], details: {} },
        scrollSpeedAge: { order: [], details: {} },
        viewTimeAge: { order: [], details: {} },
      };
    }
  },

  getInferenceFromLocalStorage(): InferenceType | null {
    const inference = sessionStorage.getItem("inference") || null;

    if (!inference) return null;

    try {
      return JSON.parse(inference) || null;
    } catch (e) {
      console.error("NOT IN JSON FORMAT");
      return null;
    }
  },

  registerPathnameInJourney() {
    const pathname = window.location.pathname;

    if (
      !window?.helium?.logs ||
      !Array.isArray(window?.helium?.logs) ||
      !window?.helium?.logs.length
    ) {
      window.helium.logs = [
        {
          time: Date.now(),
          pathname,
          funnel_stage: sessionStorage.getItem("funnelStage") ?? "viewer",
        },
      ];
    } else if (window.helium.logs.at(-1).pathname !== pathname) {
      window.helium.logs = [
        ...(window.helium.logs ?? []),
        {
          time: Date.now(),
          pathname,
          funnel_stage: sessionStorage.getItem("funnelStage") ?? "viewer",
        },
      ];
    }

    window.trackHeliumEvent = (event: any) => {
      try {
        window.helium.logs.push(event);
      } catch (e) {
        console.info("Error while tracking helium event", event);
      }
    };

    const pathnames = window.helium.logs.map(
      (log: any) => log.pathname,
    ) as string[];

    const pathnameToTimesVisited = pathnames.reduce(
      (acc, pathname) => {
        if (!pathname) return acc;

        if (!pathname.includes("/c/") && !pathname.includes("/products/")) {
          return acc;
        }

        const handle = pathname.split("/").at(-1) || "no-handle";

        if (!acc[handle]) {
          acc[handle] = 1;
        } else {
          acc[handle]++;
        }

        return acc;
      },
      {} as Record<string, number>,
    );

    console.log("SCORING ALL PRODUCTS", pathnameToTimesVisited);
    ScoringHandler.Product.scoreAllProductsFromHistoryChange(
      pathnameToTimesVisited,
    );
    // console.log("DONE SCORING ", ScoringHandler.Product.getProductScores());
  },

  tagPageElements(skipTimeout = false) {
    function getSectionsAndProducts() {
      const sections = [
        ...new Set(
          Array.from(document.querySelectorAll("section"))
            .filter((el) => el.clientWidth > 0 && el.clientHeight > 0)
            .map((section) => {
              if (!section.id) return;

              return section.id.replace("shopify-section-", "");
            })
            .filter(Boolean) as string[],
        ),
      ];

      const products = [
        ...new Set(
          Array.from(document.querySelectorAll("a"))
            .filter(
              (a) => a.href.includes("/c/") || a.href.includes("/products/"),
            )
            .map((a) => {
              if (!a.href) return;

              return decodeURIComponent(a.href.split("?").at(0) || "").trim();
            })
            .filter(Boolean) as string[],
        ),
      ];

      return { sections, products };
    }

    fetchElementTags(getSectionsAndProducts()).then(() => {});

    // if (skipTimeout) return;

    InferenceSetup.registerTimeout(
      setTimeout(() => {
        const { products, sections } = getSectionsAndProducts();

        const someProductDataNotFetched = products.some((product) => {
          const handle = product.split("/").at(-1);

          if (!handle) return;

          return !window.helium.sectionInfo[handle];
        });

        const someSectionDataNotFetched = sections.some((section) => {
          return !window.helium.sectionInfo[section];
        });

        if (someProductDataNotFetched || someSectionDataNotFetched) {
          fetchElementTags(getSectionsAndProducts()).then(() => {
            console.log("Tag fetching complete");
          });
        }
      }, 1_000),
    );
  },

  overrideHistoryChange(
    handleRouteChange = () => console.error("No route change handler provided"),
    flush = () => console.error("No flush handler provided"),
  ) {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const handleFlushBeforeNavigation = () => {
      flush();
    };

    history.pushState = function (...args) {
      window.removeEventListener("beforeunload", handleFlushBeforeNavigation);
      flush();
      originalPushState.apply(this, args);
      handleRouteChange();
      window.addEventListener("beforeunload", handleFlushBeforeNavigation);
    };

    history.replaceState = function (...args) {
      window.removeEventListener("beforeunload", handleFlushBeforeNavigation);
      flush();
      originalReplaceState.apply(this, args);
      handleRouteChange();
      window.addEventListener("beforeunload", handleFlushBeforeNavigation);
    };

    // Listen to back/forward button navigation
    window.addEventListener("popstate", () => {
      flush();
      handleRouteChange();
    });

    // Attach flush to beforeunload for direct URL navigation or closing tab
    window.addEventListener("beforeunload", handleFlushBeforeNavigation);
  },

  getIntervals(): NodeJS.Timeout[] {
    return window.heliumIntervalIds || [];
  },

  getTimeouts(): NodeJS.Timeout[] {
    return window.heliumTimeoutIds || [];
  },

  registerInterval(id: NodeJS.Timeout) {
    if (window.heliumIntervalIds && Array.isArray(window.heliumIntervalIds)) {
      window.heliumIntervalIds.push(id);
      return;
    }

    window.heliumIntervalIds = [id];
  },

  registerTimeout(id: NodeJS.Timeout) {
    if (window.heliumTimeoutIds && Array.isArray(window.heliumTimeoutIds)) {
      window.heliumTimeoutIds.push(id);
      return;
    }

    window.heliumTimeoutIds = [id];
  },

  generateRandomString() {
    const token = [
      Math.random().toString(36).substring(2),
      Math.random().toString(36).substring(2),
      Math.random().toString(36).substring(2),
      Math.random().toString(36).substring(2),
    ];

    return token.join("-");
  },

  generateTokens() {
    function getCookie(name: string) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);

      if (parts.length === 2) {
        return parts.pop()?.split(";").shift();
      }

      return null;
    }

    const userToken =
      getCookie("helium_user_token") || InferenceSetup.generateRandomString();

    const sessionToken =
      sessionStorage.getItem("sessionToken") ||
      InferenceSetup.generateRandomString();

    if (typeof window !== "undefined") {
      (window as any).helium = {
        ...(window as any).helium,
        userToken,
        sessionToken: sessionToken,
      };
    }
    sessionStorage.setItem("sessionToken", sessionToken);
    document.cookie = `helium_user_token=${userToken}; path=/`;
  },
};
